import { z } from "zod";
import { responseSchema } from "../http.js";
import { productSchema } from "./product.js";
/**
 * ═══════════════════════════════════════════════════════════
 * La tienda personalizada del franquiciatario
 *
 * (Anahí, 27-ago.) Cada franquiciatario tiene su tienda con su enlace
 * —`/shop/t/annie`, "Annieshop"— donde vende ÚNICAMENTE los productos
 * que adquirió en sus paquetes mayoristas, con el stock que esos
 * paquetes le dieron. Sin paquete no hay productos que vender.
 *
 * ── La LÓGICA del sistema viejo, portada (Anahí, 27-ago) ───
 * "Tú decides qué productos aparecen, a qué precio y quién entra":
 *  · IDENTIDAD — color de marca, mensaje del banner con su fondo,
 *    bienvenida, anuncio y teléfono de contacto (WhatsApp).
 *  · PRODUCTOS — una lista de APAGADOS (`disabledProductIds`): lo
 *    encendido se vende, lo apagado no aparece ni se cobra.
 *  · PRECIO PROPIO — `pricing[productId]` en centavos MXN. Vacío =
 *    el precio de socio del catálogo (la regla de siempre).
 *
 * ── El PRECIO de la tienda personalizada ───────────────────
 * "Se supone que el Annieshop debe ser más barata que la de It's The
 * Best": la base es el PRECIO DE SOCIO del catálogo (y el público solo
 * si un producto no tiene precio de socio); el franquiciatario puede
 * fijar el suyo por producto. El SERVIDOR resuelve el precio final en
 * cada item (`priceCents`) y el cobro aplica la misma resolución — lo
 * que se enseña es lo que se cobra.
 *
 * ── La compra de paquetes, con su letra chica ──────────────
 * `showPackages` es la decisión del franquiciatario de enseñar (o no)
 * los paquetes de It's The Best desde su tienda. La leyenda que
 * acompaña al interruptor es parte del trato: habilitarla gana los
 * bonos del plan de referidos de It's The Best — NO un sobreprecio de
 * los paquetes.
 * ═══════════════════════════════════════════════════════════
 */
/**
 * El slug de la tienda: minúsculas, números y guiones. Es la parte
 * final del enlace público, así que se escribe una vez y se comparte
 * mil — corto y sin sorpresas.
 */
export const storeSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/, "El enlace usa minúsculas, números y guiones (3 a 40 caracteres)");
/** La tienda vende en pesos: es el mercado de la casa. */
export const STORE_CURRENCY = "MXN";
/**
 * Los fondos del banner: presets con nombre, como en el sistema viejo.
 * El valor es CSS listo — el mismo degradado en la configuración (vista
 * previa) y en la vitrina.
 */
export const STORE_BANNER_BG_KEYS = [
    "violeta",
    "rosa",
    "atardecer",
    "oceano",
    "bosque",
    "tinta",
];
export const storeBannerBgSchema = z.enum(STORE_BANNER_BG_KEYS);
export const STORE_BANNER_BGS = {
    violeta: "linear-gradient(135deg, #5C4FD6 0%, #8B7CF6 100%)",
    rosa: "linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)",
    atardecer: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
    oceano: "linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)",
    bosque: "linear-gradient(135deg, #10B981 0%, #047857 100%)",
    tinta: "linear-gradient(135deg, #1F2430 0%, #3B4252 100%)",
};
/** El violeta de la casa: el acento si el franquiciatario no elige. */
export const STORE_DEFAULT_COLOR = "#5C4FD6";
/**
 * Los fondos de la PORTADA (el héroe oscuro, como el de la tienda de la
 * casa): mismos nombres que el banner, en versión profunda — el texto de
 * la portada es blanco y necesita tinta debajo, no un degradado claro.
 */
export const STORE_HERO_BGS = {
    violeta: "radial-gradient(120% 140% at 20% 0%, #4a3cb8 0%, #2c2277 55%, #171242 100%)",
    rosa: "radial-gradient(120% 140% at 20% 0%, #8f2c66 0%, #571a44 55%, #250a20 100%)",
    atardecer: "radial-gradient(120% 140% at 20% 0%, #a2541f 0%, #6b2637 55%, #2a0f24 100%)",
    oceano: "radial-gradient(120% 140% at 20% 0%, #1f5e97 0%, #143a63 55%, #081527 100%)",
    bosque: "radial-gradient(120% 140% at 20% 0%, #1f6b51 0%, #123f30 55%, #071b14 100%)",
    tinta: "radial-gradient(120% 140% at 20% 0%, #34304a 0%, #201c33 55%, #12101f 100%)",
};
const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, "El color va en hexadecimal, como #5C4FD6");
/** La identidad y la experiencia de la vitrina — todo opcional: una
 * tienda recién nacida se ve bien con los valores de la casa. */
export const storeBrandingSchema = z.object({
    brandColor: hexColorSchema.catch(STORE_DEFAULT_COLOR).default(STORE_DEFAULT_COLOR),
    /** La bajada del héroe: "Bienvenido a Annieshop" + esto. */
    welcome: z.string().trim().max(200).default(""),
    bannerMessage: z.string().trim().max(120).default(""),
    bannerBg: storeBannerBgSchema.catch("violeta").default("violeta"),
    /** Aviso libre: la próxima presentación, una promo, lo que sea. */
    announcement: z.string().trim().max(400).default(""),
    /** WhatsApp de contacto. Solo dígitos y espacios; se limpia al usar. */
    phone: z.string().trim().max(20).default(""),
    /** El fondo de la PORTADA (héroe oscuro). */
    heroBg: storeBannerBgSchema.catch("tinta").default("tinta"),
    /**
     * Los productos que FLOTAN a la derecha de la portada (la animación,
     * como el vídeo de la casa). Vacío = los primeros de su vitrina.
     */
    heroProductIds: z.array(z.string()).max(4).default([]),
});
export const storeSchema = z.object({
    slug: storeSlugSchema,
    /**
     * El enlace se personaliza UNA sola vez (Anahí, 27-ago): la tienda
     * nace con uno derivado del nombre, la dueña lo cambia con calma una
     * vez, y de ahí en adelante queda FIJO — cambiarlo a cada rato deja a
     * sus clientes visitando rutas muertas, y el servidor ya vigila que
     * jamás choque con la ruta de otro franquiciatario.
     */
    slugLocked: z.boolean(),
    /** "Annieshop": el nombre que corona la tienda. */
    name: z.string().trim().min(1, "Ponle nombre a tu tienda").max(60),
    showPackages: z.boolean(),
    /** Quién la atiende. Sale de la cuenta del franquiciatario. */
    ownerName: z.string(),
    branding: storeBrandingSchema,
});
/** Lo que el franquiciatario puede configurar de su tienda. */
export const storeInputSchema = z.object({
    slug: storeSlugSchema,
    name: z.string().trim().min(1, "Ponle nombre a tu tienda").max(60),
    showPackages: z.boolean(),
    /** Ausente = los valores de la casa: guardar solo nombre y enlace
     * nunca fue inválido. */
    branding: storeBrandingSchema.prefault({}),
    /** Los productos APAGADOS: lo del inventario que decidió no vender. */
    disabledProductIds: z.array(z.string()).max(500).default([]),
    /**
     * Su precio por producto, en centavos MXN. Piso de $1: un cero aquí
     * sería regalar producto por un dedazo. Vacío = precio del catálogo.
     * Con TOPE de entradas: un `record` sin techo es la puerta a inflar el
     * documento con miles de llaves basura.
     */
    pricing: z
        .record(z.string().max(64), z.number().int().min(100))
        .refine((value) => Object.keys(value).length <= 500, {
        message: "Demasiados precios propios",
    })
        .default({}),
    /**
     * Cuántas piezas PUBLICA de cada producto (Anahí, 1-sep). Vacío o
     * ausente = todas las que tiene.
     *
     * ── Es un tope, nunca un aumento ─────────────────────────────────
     * Sirve para guardarse piezas —apartar producto para un pedido que ya
     * viene, o no enseñar el último— y el servidor lo aplica como
     * `min(lo que tiene, lo que publica)`. Un número mayor al inventario
     * real NO crea existencias: se guarda tal cual, por si mañana llegan
     * más piezas de un paquete, pero mientras tanto no sube nada.
     *
     * Esa asimetría es deliberada. Publicar de más sería vender lo que no
     * existe: el cobro cerraría la venta, la persona pagaría, y el pedido
     * quedaría sin surtir. El tope entra en el MISMO cálculo que usa el
     * checkout, así que lo que se guarda no se puede vender por descuido.
     *
     * Cero es válido y significa "no lo enseño": es lo mismo que apagarlo
     * con el interruptor, pero conservando el precio propio que ya puso.
     */
    stockCaps: z
        .record(z.string().max(64), z.number().int().min(0).max(1_000_000))
        .refine((value) => Object.keys(value).length <= 500, {
        message: "Demasiados topes de stock",
    })
        .default({}),
});
/**
 * Un producto EN la tienda personalizada: el del catálogo, el stock que
 * el franquiciatario tiene de él, si lo tiene ENCENDIDO, y el precio ya
 * RESUELTO por el servidor (su override, o el de socio del catálogo).
 */
export const storeItemSchema = z.object({
    product: productSchema,
    /**
     * Lo que la tienda PUBLICA: `min(lo que tiene, su tope)`. Es el número
     * que ve quien compra y el que valida el cobro, así que todo lo que ya
     * leía `stock` sigue siendo correcto sin enterarse del tope.
     */
    stock: z.number().int().min(0),
    /**
     * Lo que REALMENTE tiene de sus paquetes, menos lo vendido. Solo el
     * dueño lo necesita —su panel enseña "publicas 5 de 9"—; a quien
     * compra no le sirve y por eso la vitrina no lo usa.
     */
    stockOwned: z.number().int().min(0),
    enabled: z.boolean(),
    /** El precio final en la tienda (centavos MXN). `null` si el
     * producto no tiene precio — no se puede vender. */
    priceCents: z.number().int().min(0).nullable(),
});
export const storeWithItemsSchema = z.object({
    store: storeSchema,
    items: z.array(storeItemSchema),
});
export const storeResponseSchema = responseSchema(storeWithItemsSchema);
export const STORE_ROUTES = {
    /** La tienda del franquiciatario con sesión: verla y configurarla. */
    mine: "/api/store/mine",
    /** La vitrina pública: lo que ve quien recibe el enlace. */
    public: (slug) => `/api/t/${slug}`,
};
/** La ruta pública de la vitrina en el frontend. */
export function storePath(slug) {
    return `/shop/t/${slug}`;
}
/**
 * La BASE del precio de la tienda: el de socio, y el público solo como
 * respaldo. Sobre esta base se aplica el precio propio del
 * franquiciatario — ver `resolveStorePrice`.
 */
export function storePriceOf(product, currency) {
    return product.memberPrices?.[currency] ?? product.prices[currency];
}
/**
 * EL precio final de un producto en la tienda: el que el franquiciatario
 * fijó, o la base de socio si no fijó ninguno. La vitrina lo pinta y el
 * cobro lo aplica — una sola regla en un solo sitio. Los precios propios
 * viven en MXN; en otra moneda rige la base.
 */
export function resolveStorePrice(product, pricing, currency = STORE_CURRENCY) {
    if (currency === STORE_CURRENCY) {
        const own = pricing[product.id];
        if (typeof own === "number")
            return own;
    }
    return storePriceOf(product, currency);
}
//# sourceMappingURL=store.js.map